import {
  Button,
  ButtonMenu,
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  GridTableApi,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelectField,
  numericColumn,
  ScrollableContent,
  SelectField,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  Tag,
  TextField,
  Tooltip,
  TreeSelectField,
  useGridTableApi,
  useModal,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { Link } from "react-router-dom";
import { createProductOfferingsUrl } from "src/RouteUrls";
import { priceCell, SearchBox } from "src/components";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { type HasDisplayName } from "src/generated/graphqlTypes";
import {
  type ProductOfferingCostCalculatorPage_ComputeResultFragment,
  type ProductOfferingCostCalculatorPage_LineItemFragment,
  type ProductOfferingCostCalculatorPage_ProductOfferingFragment,
  useProductOfferingCostCalculatorMetaQuery,
  useProductOfferingCostCalculatorPage_CheckoutConfigLazyQuery,
  useProductOfferingCostCalculatorPage_ComputeQuery,
} from "src/routes/developments/product-offerings/ProductOfferingCostCalculatorPage.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { type ProductOfferingCostCalculatorParams } from "src/routes/routesDef";
import { queryResult } from "src/utils";

export function ProductOfferingCostCalculatorPage() {
  const { productOfferingId, rpavId } = useParams<ProductOfferingCostCalculatorParams>();
  const query = useProductOfferingCostCalculatorMetaQuery({ variables: { id: productOfferingId, rpavId } });
  return queryResult(query, ({ productOffering }) => (
    <>
      <PageHeader
        title={`${productOffering.displayName} — Cost Calculator`}
        breadcrumb={[{ label: "Product Offerings", href: createProductOfferingsUrl() }]}
        right={
          <ReadyPlanVersionHeader
            readyPlan={productOffering}
            versionId={productOffering.version.id}
            aggregateCheckoutId={productOffering.aggregateCheckout?.id}
          />
        }
      />
      <DataView productOffering={productOffering} rpavId={rpavId} />
    </>
  ));
}

type DataViewProps = { productOffering: ProductOfferingCostCalculatorPage_ProductOfferingFragment; rpavId: string };

type OptionGroup = ProductOfferingCostCalculatorPage_ProductOfferingFragment["optionGroups"][number];
type RpoOption = OptionGroup["options"][number];

type GroupBy = "costCode" | "tradePartner" | "option" | "none";
const groupByOptions: { id: GroupBy; name: string }[] = [
  { id: "costCode", name: "Cost Code" },
  { id: "tradePartner", name: "Trade Partner" },
  { id: "option", name: "Option" },
  { id: "none", name: "None" },
];

/** Main data view that manages filters, search, and the compute query. */
function DataView(props: DataViewProps) {
  const { productOffering, rpavId } = props;
  const history = useHistory();
  const location = useLocation();
  const { openModal } = useModal();
  const api = useGridTableApi<Row>();
  const [search, setSearch] = useState<string | undefined>();
  const [groupBy, setGroupBy] = useState<GroupBy>("costCode");

  // Categorize option groups into the three tiers
  const { designPackageGroups, structuralAndAddonOptions, personalizationGroups } = useMemo(
    () => categorizeOptionGroups(productOffering.optionGroups),
    [productOffering.optionGroups],
  );

  // Build a lookup to classify option IDs into their tier + group (for URL → state hydration)
  const optionClassification = useMemo(() => {
    const map = new Map<string, { groupId: string; tier: "design" | "structural" | "personalization" }>();
    for (const group of productOffering.optionGroups) {
      const isDesign = group.forDesignExterior || group.forDesignInterior;
      if (group.forDesignPackages && !isDesign) continue;
      for (const opt of group.options) {
        if (isDesign) {
          map.set(opt.id, { groupId: group.id, tier: "design" });
        } else if (opt.type.isStructuralV2 || opt.type.isAddOnV2) {
          map.set(opt.id, { groupId: group.id, tier: "structural" });
        } else {
          map.set(opt.id, { groupId: group.id, tier: "personalization" });
        }
      }
    }
    return map;
  }, [productOffering.optionGroups]);

  // Parse initial option IDs from the `?options=1,2,3` query param (only on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialOptionIds = useMemo(() => parseOptionsParam(location.search), []);

  // State: one selected RPO ID per design-package RPOG (single-select)
  // Hydrate from URL params if present, otherwise default to the first active option per group
  const [designSelections, setDesignSelections] = useState<Record<string, string | undefined>>(() => {
    const selections: Record<string, string | undefined> = {};
    for (const id of initialOptionIds) {
      const info = optionClassification.get(id);
      if (info?.tier === "design") selections[info.groupId] = id;
    }
    // Only auto-select package options on a fresh page load with no explicit URL selections.
    if (initialOptionIds.isEmpty) {
      for (const group of designPackageGroups) {
        if (!selections[group.id]) {
          const firstActive = group.options.find((o) => o.active);
          if (firstActive) selections[group.id] = firstActive.id;
        }
      }
    }
    return selections;
  });
  // State: selected RPO IDs for structural & add-ons (multi-select)
  const [structuralIds, setStructuralIds] = useState<string[]>(() =>
    initialOptionIds.filter((id) => optionClassification.get(id)?.tier === "structural"),
  );
  // State: selected RPO IDs for personalization (multi-select)
  const [personalizationIds, setPersonalizationIds] = useState<string[]>(() =>
    initialOptionIds.filter((id) => optionClassification.get(id)?.tier === "personalization"),
  );

  // Merge all selected RPO IDs
  const readyPlanOptionIds = useMemo(() => {
    return [...Object.values(designSelections), ...structuralIds, ...personalizationIds].compact();
  }, [designSelections, structuralIds, personalizationIds]);

  // Sync selected options back to the `?options=` query param
  const syncOptionsToUrl = useCallback(
    (ids: string[]) => {
      const params = new URLSearchParams(location.search);
      if (ids.isEmpty) {
        params.delete("options");
      } else {
        // I.e. ["rpo:1", "rpo:2"] → "1-2"
        params.set("options", ids.map((id) => id.replace("rpo:", "")).join("-"));
      }
      const qs = params.toString();
      history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
    },
    [history, location.pathname, location.search],
  );

  useEffect(() => {
    syncOptionsToUrl(readyPlanOptionIds);
  }, [readyPlanOptionIds, syncOptionsToUrl]);

  /** Distributes a flat list of option IDs into the three selection state buckets. */
  const loadOptionIds = useCallback(
    (ids: string[]) => {
      const newDesign: Record<string, string | undefined> = {};
      const newStructural: string[] = [];
      const newPersonalization: string[] = [];
      for (const id of ids) {
        const info = optionClassification.get(id);
        if (!info) continue;
        if (info.tier === "design") newDesign[info.groupId] = id;
        else if (info.tier === "structural") newStructural.push(id);
        else newPersonalization.push(id);
      }
      setDesignSelections(newDesign);
      setStructuralIds(newStructural);
      setPersonalizationIds(newPersonalization);
    },
    [optionClassification],
  );

  // Map selected IDs back to RPO objects for column headers, sorted by their group's order
  const allOptionsWithGroupOrder = useMemo(
    () => productOffering.optionGroups.flatMap((g) => g.options.map((o) => ({ ...o, groupOrder: g.order }))),
    [productOffering.optionGroups],
  );
  const selectedOptions = useMemo(
    () => allOptionsWithGroupOrder.filter((o) => readyPlanOptionIds.includes(o.id)).sortBy((o) => o.groupOrder),
    [allOptionsWithGroupOrder, readyPlanOptionIds],
  );
  /** Lookup from option id to its group's sort order, used for "Group By: Option". */
  const optionOrderById = useMemo(
    () => new Map(allOptionsWithGroupOrder.map((o) => [o.id, o.groupOrder])),
    [allOptionsWithGroupOrder],
  );

  const skip = readyPlanOptionIds.isEmpty;
  const query = useProductOfferingCostCalculatorPage_ComputeQuery({
    variables: { readyPlanId: productOffering.id, readyPlanOptionIds, versionId: rpavId },
    skip,
  });

  // Refetch when contingency markup changes so cost updates without other interaction
  useEffect(() => {
    if (!skip) void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productOffering.contingencyMarkupPercentage]);

  const csvPrefixRows: string[][] = [
    ["Plan", productOffering.displayName],
    // TODO: add computeProgramData back once the query field is uncommented
    // ["Sellable Sqft", String(query.data?.computeProgramData?.sellableSqft ?? "N/A")],
    ["Version", productOffering.version.displayVersion],
  ];
  const csvFileName =
    [productOffering.displayName.replaceAll(".", "_"), productOffering.version.displayVersion].join("_") + ".csv";

  return (
    <>
      <OptionSelectors
        designPackageGroups={designPackageGroups}
        designSelections={designSelections}
        onDesignChange={(groupId, value) => setDesignSelections((prev) => ({ ...prev, [groupId]: value }))}
        structuralAndAddonOptions={structuralAndAddonOptions}
        structuralIds={structuralIds}
        onStructuralChange={setStructuralIds}
        personalizationGroups={personalizationGroups}
        personalizationIds={personalizationIds}
        onPersonalizationChange={setPersonalizationIds}
        optionGroups={productOffering.optionGroups}
        selectedOptionIds={readyPlanOptionIds}
        includedOptions={query.data?.computeReadyPlanCost.includedOptions}
        configurationErrors={query.data?.computeReadyPlanCost.configurationErrors}
      />
      <div css={Css.df.jcsb.aic.my1.$}>
        <div css={Css.df.aic.gap2.$}>
          <SearchBox onSearch={setSearch} />
          <SelectField
            label="Group By"
            value={groupBy}
            options={groupByOptions}
            onSelect={(v) => setGroupBy(v ?? "costCode")}
            sizeToContent
            labelStyle="inline"
            compact
          />
        </div>
        <ButtonMenu
          trigger={{ icon: "verticalDots" }}
          placement="right"
          items={[
            { label: "Download CSV", onClick: () => api.downloadToCsv(csvFileName) },
            {
              label: "Load Checkout",
              onClick: () => openModal({ content: <LoadCheckoutModal loadOptionIds={loadOptionIds} /> }),
            },
          ]}
        />
      </div>
      {!skip &&
        queryResult(query, ({ computeReadyPlanCost }) => (
          <CostView
            api={api}
            result={computeReadyPlanCost}
            search={search}
            options={selectedOptions}
            csvPrefixRows={csvPrefixRows}
            groupBy={groupBy}
            optionOrderById={optionOrderById}
          />
        ))}
    </>
  );
}

type IncludedOption = { id: string; displayName: string };

type OptionSelectorsProps = {
  designPackageGroups: OptionGroup[];
  designSelections: Record<string, string | undefined>;
  onDesignChange: (groupId: string, value: string | undefined) => void;
  structuralAndAddonOptions: RpoOption[];
  structuralIds: string[];
  onStructuralChange: (ids: string[]) => void;
  personalizationGroups: { id: string; name: string; children: { id: string; name: string }[] }[];
  personalizationIds: string[];
  onPersonalizationChange: (ids: string[]) => void;
  optionGroups: ProductOfferingCostCalculatorPage_ProductOfferingFragment["optionGroups"];
  selectedOptionIds: string[];
  includedOptions?: IncludedOption[];
  configurationErrors?: string[];
};

/** Renders the option selection UI in two rows. */
function OptionSelectors(props: OptionSelectorsProps) {
  const {
    designPackageGroups,
    designSelections,
    onDesignChange,
    structuralAndAddonOptions,
    structuralIds,
    onStructuralChange,
    personalizationIds,
    onPersonalizationChange,
    personalizationGroups,
    optionGroups,
    selectedOptionIds,
    includedOptions,
    configurationErrors,
  } = props;
  const { openModal } = useModal();

  return (
    <div css={Css.df.fdc.gap1.$}>
      {/* Row 1: Design package RPOGs — one single-select per group */}
      <div css={Css.df.gap2.flexWrap("wrap").$}>
        {designPackageGroups.map((group) => (
          <SelectField
            key={group.id}
            label={group.name}
            value={designSelections[group.id]}
            onSelect={(value) => onDesignChange(group.id, value)}
            options={group.options.filter((o) => o.active).sortBy((o) => o.displayName)}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.displayName}
            nothingSelectedText="—"
            sizeToContent
          />
        ))}
      </div>
      {/* Row 2: Structural & Add-Ons + Personalization multi-selects */}
      {(structuralAndAddonOptions.nonEmpty || personalizationGroups.nonEmpty) && (
        <div css={Css.df.gap2.flexWrap("wrap").$}>
          {structuralAndAddonOptions.nonEmpty && (
            <MultiSelectField
              label="Structural & Add-Ons"
              values={structuralIds}
              onSelect={onStructuralChange}
              options={structuralAndAddonOptions}
              getOptionValue={(o) => o.id}
              getOptionLabel={(o) => o.displayName}
              nothingSelectedText="—"
              sizeToContent
            />
          )}
          {personalizationGroups.nonEmpty && (
            <TreeSelectField
              label="Personalization"
              values={personalizationIds}
              onSelect={(response) => onPersonalizationChange(response.leaf.values)}
              options={personalizationGroups}
              getOptionValue={(o) => o.id}
              getOptionLabel={(o) => o.name}
              groupOptions={personalizationGroups.map((g) => g.id)}
              nothingSelectedText="—"
              sizeToContent
              defaultCollapsed
            />
          )}
          {/* Included Options — the options the server resolved for the selected configuration */}
          {includedOptions && includedOptions.nonEmpty && (
            <div css={Css.df.aic.mt1.$}>
              <Button
                variant="text"
                label={`${includedOptions.length} Included Options`}
                onClick={() =>
                  openModal({
                    size: "xl",
                    content: (
                      <IncludedOptionsModal
                        optionGroups={optionGroups}
                        selectedOptionIds={selectedOptionIds}
                        includedOptions={includedOptions}
                      />
                    ),
                  })
                }
              />
            </div>
          )}
          {/* Configuration errors from invalid option combinations */}
          {configurationErrors && configurationErrors.nonEmpty && (
            <div css={Css.df.aic.gap1.mt1.$}>
              <Tooltip
                title={
                  <ul css={Css.pl2.$}>
                    {configurationErrors.map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                }
              >
                <Tag text={`${configurationErrors.length} Configuration Errors`} type="warning" />
              </Tooltip>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type CostViewProps = {
  api: GridTableApi<Row>;
  result: ProductOfferingCostCalculatorPage_ComputeResultFragment;
  csvPrefixRows: string[][];
  search: string | undefined;
  options: HasDisplayName[];
  groupBy: GroupBy;
  optionOrderById: Map<string, number>;
};

/** Renders the cost breakdown GridTable. */
function CostView(props: CostViewProps) {
  const { api, result, search, options, csvPrefixRows, groupBy, optionOrderById } = props;
  const columns = useMemo(() => createColumns(options), [options]);
  const rows = useMemo(
    () => createRows(options, result, groupBy, optionOrderById),
    [options, result, groupBy, optionOrderById],
  );
  return (
    <ScrollableContent virtualized>
      <GridTable
        api={api}
        columns={columns}
        rows={rows}
        filter={search}
        csvPrefixRows={csvPrefixRows}
        sorting={{ on: "client" }}
        as="virtual"
      />
    </ScrollableContent>
  );
}

type Totals = { totalCostInCents: number; totalByOptions: Map<string, number> };

type GroupRowData = { id: string; displayName: string; totalCostInCents: number; totalByOptions: Map<string, number> };

type Row =
  | { kind: "header" }
  | { kind: "totals"; data: Totals }
  | { kind: "group"; data: GroupRowData }
  | { kind: "tliv"; data: TlivEntry };

/** Creates the grid columns: fixed columns + one dynamic column per selected RPO. */
function createColumns(options: HasDisplayName[]): GridColumn<Row>[] {
  const groupColumn = column<Row>({
    header: "Group",
    group: ({ displayName }) => displayName,
    tliv: emptyCell,
    totals: emptyCell,
    showIn: "csv",
  });
  const nameColumn = column<Row>({
    header: "Name",
    group: ({ id, displayName }) => ({
      content: () => (
        <Tooltip title={id} placement="left">
          {displayName}
        </Tooltip>
      ),
      value: displayName,
    }),
    tliv: (li) => ({
      content: () => (
        <Tooltip title={li.id} placement="left">
          {li.displayName}
        </Tooltip>
      ),
      value: li.displayName,
    }),
    totals: emptyCell,
    w: "600px",
  });
  const optionsColumn = column<Row>({
    header: "Options",
    group: emptyCell,
    tliv: (li) => ({
      content: () => (
        <div css={Css.df.fdc.$}>
          {li.options.map((o) => (
            <div key={o.id}>{o.code}</div>
          ))}
        </div>
      ),
      value: li.options.map((o) => o.code).join(", "),
    }),
    totals: emptyCell,
    w: "250px",
  });
  const costSourceColumn = column<Row>({
    header: "Cost Source",
    group: emptyCell,
    tliv: (li) => {
      const revision = li.preferredBidContractLineItem?.revision;
      if (!revision) return emptyCell;
      return {
        content: () => <Link to={revision.blueprintUrl.path}>{revision.displayName}</Link>,
        value: revision.displayName,
      };
    },
    totals: emptyCell,
    w: "400px",
  });
  const costColumn = numericColumn<Row>({
    header: "Total Cost",
    group: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
    tliv: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
    totals: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
    w: "120px",
  });

  // TODO: add back base + per-option columns once we derive option columns from the result data
  // const baseColumn = numericColumn<Row>({
  //   header: "Base",
  //   group: (data) => {
  //     const claimedByOptions = [...data.totalByOptions.values()].sum();
  //     return priceCell({ valueInCents: data.totalCostInCents - claimedByOptions });
  //   },
  //   tliv: (li) => (li.options.length === 0 ? priceCell({ valueInCents: li.totalCostInCents }) : emptyCellDash),
  //   totals: (totals) => {
  //     const claimedByOptions = [...totals.totalByOptions.values()].sum();
  //     return priceCell({ valueInCents: totals.totalCostInCents - claimedByOptions });
  //   },
  //   w: "120px",
  // });
  //
  // const optionColumns = options.map((rpo) =>
  //   numericColumn<Row>({
  //     id: rpo.id,
  //     header: rpo.displayName,
  //     group: ({ totalByOptions }) => {
  //       const valueInCents = totalByOptions.get(rpo.id) ?? 0;
  //       return valueInCents ? priceCell({ valueInCents }) : emptyCellDash;
  //     },
  //     tliv: (li) =>
  //       li.options.some((o) => o.id === rpo.id) ? priceCell({ valueInCents: li.totalCostInCents }) : emptyCellDash,
  //     totals: (totals) => priceCell({ valueInCents: totals.totalByOptions.get(rpo.id) ?? 0 }),
  //     w: "120px",
  //   }),
  // );

  return [
    collapseColumn<Row>({ totals: emptyCell }),
    groupColumn,
    nameColumn,
    optionsColumn,
    costSourceColumn,
    costColumn,
  ];
}

/** A tliv with context from its parent ProtoProjectItem, for grouping. */
type TlivEntry = ProductOfferingCostCalculatorPage_LineItemFragment & {
  rowId: string;
  costCodeId: string;
  costCodeName: string;
  tradePartnerId: string;
  tradePartnerName: string;
  /** The last option (by optionGroup.order), used for "Group By: Option". */
  lastOptionId: string;
  lastOptionCode: string;
};

/** Creates the grid rows, optionally grouped by cost code, trade partner, or option. */
function createRows(
  options: HasDisplayName[],
  result: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  groupBy: GroupBy,
  optionOrderById: Map<string, number>,
): GridDataRow<Row>[] {
  // Immediately flatMap proto project items → tliv entries with parent context
  const entries: TlivEntry[] = result.protoProjectItems.flatMap((pi, piIdx) => {
    const lineItems = [pi.itemTemplateItem, ...pi.modifyingTemplateItems].compact();
    const tp = pi.preferredPotentialBidContractLineItem?.bidContractLineItem?.revision.bidContract.tradePartner;
    /** Shared grouping dimensions from the parent ProtoProjectItem. */
    function dims(opts: readonly { id: string; code: string }[]) {
      const lastOpt = lastOptionByGroupOrder(opts, optionOrderById);
      return {
        costCodeId: pi.item.costCode.id,
        costCodeName: pi.item.costCode.displayName,
        tradePartnerId: tp?.id ?? "unassigned",
        tradePartnerName: tp?.name ?? "Homebound Internal Estimate",
        lastOptionId: lastOpt?.id ?? "base",
        lastOptionCode: lastOpt?.code ?? "Base House",
      };
    }
    if (lineItems.nonEmpty) {
      return lineItems.map((li) => ({ ...li, rowId: li.id, ...dims(li.options) }) as TlivEntry);
    }
    // Synthetic entry for proto items with no backing line items (e.g. contingency)
    return [
      {
        id: "tli:0",
        rowId: `pi-${piIdx}-tli:0`,
        displayName: pi.name,
        options: [],
        preferredBidContractLineItem: undefined,
        totalCostInCents: pi.totalCostInCents,
        ...dims([]),
      } as TlivEntry,
    ];
  });

  const totals: Totals = {
    totalCostInCents: result.totalCostInCents,
    totalByOptions: totalByOptions(entries, options),
  };

  if (groupBy === "none") {
    return [{ kind: "totals", id: "totals", data: totals }, simpleHeader, ...toTlivRows(entries)];
  }

  // Group entries by the selected dimension
  const grouped = entries.groupBy((e) => entryGroupKey(e, groupBy));

  const groupEntries = Object.entries(grouped);
  // When grouping by option, put "Base House" first
  if (groupBy === "option") groupEntries.sort(([a], [b]) => (a === "base" ? -1 : b === "base" ? 1 : 0));

  const groupRows: GridDataRow<Row>[] = groupEntries.map(([groupId, items]) => ({
    kind: "group" as const,
    id: groupId,
    data: {
      id: groupId,
      displayName: entryGroupName(items[0], groupBy),
      totalCostInCents: items.map((e) => e.totalCostInCents).sum(),
      totalByOptions: totalByOptions(items, options),
    },
    children: toTlivRows(items),
  }));

  return [{ kind: "totals", id: "totals", data: totals }, simpleHeader, ...groupRows];
}

/** Converts tliv entries into GridDataRows. */
function toTlivRows(entries: TlivEntry[]): GridDataRow<Row>[] {
  return entries.map((e) => ({ kind: "tliv" as const, id: e.rowId, data: e }));
}

/** Computes per-option cost totals from a set of tliv entries. */
function totalByOptions(entries: TlivEntry[], options: HasDisplayName[]): Map<string, number> {
  return new Map(
    options.map((rpo) => [
      rpo.id,
      entries
        .filter((e) => e.options.some((o) => o.id === rpo.id))
        .map((e) => e.totalCostInCents)
        .sum(),
    ]),
  );
}

/** Returns the group key for a tliv entry based on the active groupBy mode. */
function entryGroupKey(entry: TlivEntry, groupBy: GroupBy): string {
  if (groupBy === "costCode") return entry.costCodeId;
  if (groupBy === "tradePartner") return entry.tradePartnerId;
  return entry.lastOptionId;
}

/** Returns the group display name for a tliv entry based on the active groupBy mode. */
function entryGroupName(entry: TlivEntry, groupBy: GroupBy): string {
  if (groupBy === "costCode") return entry.costCodeName;
  if (groupBy === "tradePartner") return entry.tradePartnerName;
  return entry.lastOptionCode;
}

/** Returns the last option when sorted by optionGroup.order. */
function lastOptionByGroupOrder(opts: readonly { id: string; code: string }[], optionOrderById: Map<string, number>) {
  return [...opts].sortBy((o) => optionOrderById.get(o.id) ?? 0).at(-1);
}

type IncludedOptionsModalProps = {
  optionGroups: ProductOfferingCostCalculatorPage_ProductOfferingFragment["optionGroups"];
  selectedOptionIds: string[];
  includedOptions: IncludedOption[];
};

/** Modal listing all options (selected + included) with their option group, sorted by group order. */
function IncludedOptionsModal(props: IncludedOptionsModalProps) {
  const { optionGroups, selectedOptionIds, includedOptions } = props;
  // Union of user-selected and server-resolved option IDs
  const allOptionIds = new Set([...selectedOptionIds, ...includedOptions.map((o) => o.id)]);

  const rows = optionGroups
    .sortBy((g) => g.order)
    .flatMap((g) => g.options.filter((o) => allOptionIds.has(o.id)).map((o) => ({ ...o, groupName: g.name })));

  const columns: GridColumn<SimpleHeaderAndData<(typeof rows)[number]>>[] = [
    column({ header: "Option Group", data: ({ groupName }) => groupName, w: "300px" }),
    column({ header: "Option Name", data: ({ displayName }) => displayName }),
  ];

  return (
    <>
      <ModalHeader>Options ({rows.length})</ModalHeader>
      <ModalBody>
        <GridTable columns={columns} rows={simpleDataRows(rows)} />
      </ModalBody>
    </>
  );
}

type LoadCheckoutModalProps = { loadOptionIds: (ids: string[]) => void };

/** Modal that loads selected options from a checkout config by ID. */
function LoadCheckoutModal(props: LoadCheckoutModalProps) {
  const { loadOptionIds } = props;
  const { closeModal } = useModal();
  const [configId, setConfigId] = useState<string | undefined>();
  const [fetchCheckout, { loading, error }] = useProductOfferingCostCalculatorPage_CheckoutConfigLazyQuery();

  async function handleLoad() {
    if (!configId) return;
    const { data } = await fetchCheckout({ variables: { id: configId } });
    if (data) {
      loadOptionIds(data.checkoutConfig.options.map((o) => o.id));
      closeModal();
    }
  }

  return (
    <>
      <ModalHeader>Load Checkout Config</ModalHeader>
      <ModalBody>
        <TextField label="Checkout Config ID" value={configId} onChange={setConfigId} placeholder="ccfg:1234" />
        {error && <div css={Css.sm.red600.mt1.$}>Failed to load: {error.message}</div>}
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" label="Cancel" onClick={closeModal} />
        <Button label="Load" disabled={!configId || loading} onClick={handleLoad} />
      </ModalFooter>
    </>
  );
}

/** Categorizes option groups into design-package, structural/add-on, and personalization tiers. */
function categorizeOptionGroups(optionGroups: OptionGroup[]) {
  // Tier 1: Design package RPOGs — one single-select per group
  const designPackageGroups = optionGroups
    .filter((g) => g.forDesignExterior || g.forDesignInterior)
    .sortBy((g) => g.order);

  // Tier 2 & 3: Non-design-package RPOGs, split by type. Sorted by RPOG order, then by displayName within each group.
  const nonDesignGroups = optionGroups.filter((g) => !g.forDesignPackages).sortBy((g) => g.order);
  const allNonDesignOptions = nonDesignGroups.flatMap((g) =>
    g.options.filter((o) => o.active).sortBy((o) => o.displayName),
  );

  // I.e. "Add Bedroom", "Add Garage Bay", structural upgrades
  const structuralAndAddonOptions = allNonDesignOptions.filter((o) => o.type.isStructuralV2 || o.type.isAddOnV2);

  // I.e. personalization product/package options
  const personalizationOptions = allNonDesignOptions.filter((o) => !o.type.isStructuralV2 && !o.type.isAddOnV2);

  // Personalization groups as a tree for TreeSelectField (groups → options)
  const personalizationGroups = nonDesignGroups
    .filter((g) => g.options.some((o) => o.active && !o.type.isStructuralV2 && !o.type.isAddOnV2))
    .sortBy((g) => g.order)
    .map((g) => ({
      id: g.id,
      name: g.name,
      children: g.options
        .filter((o) => o.active && !o.type.isStructuralV2 && !o.type.isAddOnV2)
        .sortBy((o) => o.displayName)
        .map((o) => ({ id: o.id, name: o.displayName })),
    }));

  return { designPackageGroups, structuralAndAddonOptions, personalizationOptions, personalizationGroups };
}

/** Parses `?options=1,2,3` into `["rpo:1", "rpo:2", "rpo:3"]`. */
function parseOptionsParam(search: string): string[] {
  const raw = new URLSearchParams(search).get("options");
  if (!raw) return [];
  return raw.split("-").map((id) => `rpo:${id.trim()}`);
}
